<!-- MetaPixel.vue component -->
<template>
  <!-- Noscript fallback for the pixel - only if consent given -->
  <noscript v-if="pixelId && hasConsent">
    <img height="1" width="1" style="display:none"
      :src="`https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`"
    />
  </noscript>
</template>

<script setup lang="ts">
import { onMounted, watch, defineProps, ref, computed } from 'vue';

const props = defineProps({
  pixelId: {
    type: String,
    required: true
  }
});

const isPixelInitialized = ref(false);
const consentGiven = ref(false); // Track consent state

// Check consent from URL parameters or internal state
const hasConsent = computed(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const trackingOptIn = urlParams.get('trackingOptIn'); 
  
  // If we're in WebView context (trackingOptIn parameter exists)
  if (trackingOptIn !== null) {
    return trackingOptIn === 'true';
  }
  
  // If we're on standalone webpage, use internal consent state
  return consentGiven.value;
});

// Initialize pixel
const initializePixel = (id: string) => {
  if (!id || !hasConsent.value || isPixelInitialized.value) return;
 
  // Initialize Facebook Pixel
  // @ts-ignore
  !function(f: any, b: any, e: any, v: any, n: any, t: any, s: any) {
    if(f.fbq) return; 
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    };
    if(!f._fbq) f._fbq = n; 
    n.push = n; 
    n.loaded = !0; 
    n.version = '2.0';
    n.queue = []; 
    t = b.createElement(e); 
    t.async = !0;
    t.src = v; 
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s)
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  // Grant consent and initialize
  (window as any).fbq('consent', 'grant');
  (window as any).fbq('init', id);
  (window as any).fbq('track', 'PageView');
  
  isPixelInitialized.value = true;
};

// Revoke pixel consent and clear
const revokePixelConsent = () => {
  if ((window as any).fbq && isPixelInitialized.value) {
    (window as any).fbq('consent', 'revoke');
    isPixelInitialized.value = false;
  }
};

// Handle consent changes
const handleConsentChange = () => {
  if (hasConsent.value && props.pixelId) {
    initializePixel(props.pixelId);
  } else {
    revokePixelConsent();
  }
};

// Initialize when component mounts
onMounted(() => {
  window.addEventListener('message', handleConsentMessage);
  handleConsentChange();
   if(!window.ReactNativeWebView) {
    // If not in WebView, check local storage
    consentGiven.value = localStorage.getItem('trackingOptIn') === 'true';
  }
});

// Watch for pixel ID changes
watch(() => props.pixelId, (newId) => {
  if (newId && hasConsent.value) {
    // Reset initialization flag when pixel ID changes
    isPixelInitialized.value = false;
    initializePixel(newId);
  }
});

// Watch for consent changes
watch(hasConsent, () => {
  handleConsentChange();
});

// Listen for consent updates (from banner or React Native)
const handleConsentMessage = (event: MessageEvent) => {
  if (event.data && typeof event.data === 'string') {
    try {
      const data = JSON.parse(event.data);
      if (data.type === 'CONSENT_UPDATED') {
        // Update internal consent state for standalone webpage
        consentGiven.value = data.payload.action === 'accept';
      }
    } catch (e) {
      // Ignore non-JSON messages
    }
  }
};

// Cleanup
import { onUnmounted } from 'vue';
onUnmounted(() => {
  window.removeEventListener('message', handleConsentMessage);
});
</script>